import type { GroupLifeSubStipulator } from '@/features/quotes/context/group-life-quote.context';
import { useGroupLifeQuote } from '@/features/quotes/context/group-life-quote.context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useCalculateGroupLifeQuote } from '@/features/quotes/hooks/group-life/use-calculate-group-life-quote.hook';
import { useGroupLifeCalculation } from '@/features/quotes/hooks/group-life/use-group-life-calculation.hook';

import {
  useGroupLifeLivesCount,
  useInitiateGroupLifeProposal,
} from '@/features/proposals/hooks/group-life';
import { useGenerateGroupLifeQuotePdf } from '@/features/quotes/hooks/group-life/use-generate-group-life-quote-pdf.hook';
import { useGroupLifeQuoteDetails } from '@/features/quotes/hooks/group-life/use-group-life-quote-details.hook';
import { useQuotationVersionId } from '@/features/quotes/hooks/group-life/use-quotation-version-id.hook';

import { SummaryLivesGrid } from '@/features/proposal-analyses/pme/summary-lives-grid.component';
import { resolveGroupLifeMonthlyPremium } from '@/features/quotes/utils/group-life/monthly-premium';
import { formatPartyDocument } from '@/shared/utils';
import {
  isTerminalPortalStatus,
  PortalStatus,
  TERMINAL_QUOTE_STATUS_LABELS,
} from '@/shared/enums/portal-status.enum';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { useIsUnderwriterUser } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Calculator,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileDown,
  FileText,
  LogOut,
  Pencil,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { UnderAnalysisInfoModal } from '../under-analysis-info-modal.component';
import { CloneVersionsSection } from './clone-versions-section.component';
import { ConfirmFinalizationModal } from './confirm-finalization.modal';

type Props = {
  onBack: () => void;
  onExit: () => void;
  /**
   * Cotação reaberta pelo "Visualizar" (`quoteFlow=view`), em vez de recém
   * calculada dentro do wizard.
   */
  isViewMode?: boolean;
};

const STIPULATOR_KEY = '__stipulator__';

const PRODUCT_LABEL = 'PME';

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function insuranceTypeLabel(type?: '1' | '3' | '4'): string {
  switch (type) {
    case '3':
      return 'Renovação';
    case '4':
      return 'Transferência';
    case '1':
    default:
      return 'Novo';
  }
}

/** Target do estipulante master nos endpoints de vidas do Group Life. */
const STIPULATOR_TARGET = '__STIPULATOR__';

export function SummaryView({ onBack, onExit, isViewMode = false }: Props) {
  const { stipulator, subStipulators, commercialization } = useGroupLifeQuote();
  const { policyId, proposalNumber, productId } = useQuoteParams();
  // Só a contagem de vidas por target: as grades já paginam no servidor.
  const livesTargetIds = useMemo(
    () => [STIPULATOR_TARGET, ...subStipulators.map(s => s.id)],
    [subStipulators]
  );
  const { total: totalLives, totalByTarget: livesByTarget } =
    useGroupLifeLivesCount({ policyId, targetIds: livesTargetIds });
  const hasLives = totalLives > 0;
  const stipulatorDocument = formatPartyDocument(stipulator?.registerNumber, {
    isCaepf: stipulator?.isCaepf,
  });
  const { isUnderwriterUser } = useIsUnderwriterUser();
  const showUnderwritingPercents = isUnderwriterUser === true;
  const navigate = useNavigate();
  const initiateProposal = useInitiateGroupLifeProposal();
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const proposalWriteGuard = useSelectedBrokerWriteGuard('proposal');
  const quoteActionGuard = useSelectedBrokerWriteGuard('quote', 'other');
  const generatePdf = useGenerateGroupLifeQuotePdf(policyId ?? '');

  // Prêmio REAL vindo do InsureMO (gravado no Step 4). O hook refetcha ao
  // montar o Step 5, então reflete sempre o último cálculo.
  const {
    data: calc,
    isLoading: isCalcLoading,
    isError: isCalcError,
    error: calcError,
  } = useGroupLifeCalculation();

  // Sem cálculo (ex.: cotação clonada antes do primeiro recálculo) o PDF
  // sairia sem prêmio — "Gerar PDF" fica desabilitado e o botão "Calcular"
  // dispara o cálculo do prêmio.
  const calculateQuote = useCalculateGroupLifeQuote();
  const isCalculated = Boolean(calc);
  const showCalculateButton = !isCalcLoading && !isCalculated && hasLives;

  const handleCalculate = () => {
    if (calculateQuote.isPending) return;
    calculateQuote.mutate(undefined, {
      onSuccess: () =>
        toaster.create({
          type: 'success',
          title: 'Cotação calculada',
          description: 'O prêmio foi calculado com sucesso.',
        }),
      onError: err =>
        toaster.create({
          type: 'error',
          title: 'Erro ao calcular',
          description: err instanceof Error ? err.message : 'Tente novamente.',
        }),
    });
  };

  const { data: quoteDetails, isLoading: isLoadingQuoteDetails } =
    useGroupLifeQuoteDetails();
  /**
   * Status ainda desconhecido: o snapshot é buscado com
   * `refetchOnMount: always`, então o primeiro paint não tem `quoteDetails`.
   *
   * É um terceiro estado, e não um sinônimo de "terminal". Enquanto ele vale, o
   * rodapé não decide nada: mostra um spinner no lugar dos botões. Já erramos
   * dos dois lados aqui — tratar indefinido como NÃO terminal abria a tela
   * destravada (Voltar + Editar) por um instante; tratar como terminal fazia
   * piscar o "Sair" antes do "Gerar PDF"/"Gerar Proposta".
   */
  const isStatusUnknown = isLoadingQuoteDetails;

  /**
   * Resumo de uma VERSÃO da cotação (`?quotationVersionId=`), aberto a partir
   * do versionamento — inclusive o da análise da PROPOSTA, quando o subscritor
   * clica numa versão da fase de cotação.
   *
   * É um retrato histórico: nada ali pode ser alterado nem originar uma nova
   * proposta. A tela fica só em consulta — sobra o "Sair".
   */
  const isVersionSnapshot = Boolean(useQuotationVersionId());
  const isUnderAnalysis =
    Number(quoteDetails?.statusPortal) === PortalStatus.QuoteUnderAnalysis;
  const isRejectedByAnalysis =
    Number(quoteDetails?.statusPortal) === PortalStatus.QuoteClosed;
  // Encerrada, expirada, excluída ou recusada — em qualquer um deles a cotação
  // não é mais editável.
  const isTerminalStatus = isTerminalPortalStatus(quoteDetails?.statusPortal);
  const terminalStatusLabel =
    TERMINAL_QUOTE_STATUS_LABELS[Number(quoteDetails?.statusPortal)] ??
    'encerrada';
  const rejectionReason = quoteDetails?.rejectionReason?.trim();
  const blurPremiumValueClass = isUnderAnalysis ? 'blur-[6px] select-none' : '';

  const [isUnderAnalysisModalOpen, setIsUnderAnalysisModalOpen] =
    useState(false);
  const hasShownUnderAnalysisModalRef = useRef(false);

  useEffect(() => {
    if (!hasShownUnderAnalysisModalRef.current && isUnderAnalysis) {
      hasShownUnderAnalysisModalRef.current = true;
      setIsUnderAnalysisModalOpen(true);
    }
  }, [isUnderAnalysis]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [premiumOpen, setPremiumOpen] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalized, setFinalized] = useState(false);

  /**
   * O "Finalizar" é um gate de SESSÃO: não chama nada no backend, só libera
   * "Gerar PDF" e "Gerar Proposta" depois que o corretor confere o resumo.
   *
   * Numa cotação reaberta esse gate não faz sentido — ela já foi conferida (e,
   * quando passou pela subscrição, já foi aprovada). Sem isto, abrir uma
   * cotação aprovada pelo "Visualizar" caía no botão "Finalizar" de novo, em
   * vez de nas ações que o corretor precisa.
   */
  const canGenerateDocuments = finalized || isViewMode;

  const contractType = insuranceTypeLabel(stipulator?.insuranceType);

  // O card mostra o prêmio MENSAL da fonte única (`calculation.monthly`,
  // calculado pelo back) — o mesmo número da proposta, da análise e dos PDFs.
  const monthly = hasLives ? resolveGroupLifeMonthlyPremium(calc) : null;
  const monthlyPremium = monthly?.total ?? 0;
  const monthlyIof = monthly?.iof ?? 0;
  const monthlyPremiumBeforeIof = monthly?.beforeIof ?? 0;

  // Itens da accordion: Estipulante (master, sempre) + cada Subestipulante.
  const accordionKeys = useMemo(() => {
    const keys: string[] = [STIPULATOR_KEY];
    for (const s of subStipulators) keys.push(s.id);
    return keys;
  }, [subStipulators]);

  const allOpen =
    accordionKeys.length > 0 && accordionKeys.every(k => expanded[k]);

  const toggleAll = () => {
    const next: Record<string, boolean> = {};
    for (const k of accordionKeys) next[k] = !allOpen;
    setExpanded(next);
  };

  const toggleExpanded = (id: string) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const confirmFinalize = () => {
    if (isTerminalStatus) return;
    setConfirmOpen(false);
    setFinalized(true);
  };

  const handleGeneratePdf = () => {
    if (isTerminalStatus) {
      toaster.create({
        type: 'warning',
        title: `Cotação ${terminalStatusLabel}`,
        description: `A cotação está ${terminalStatusLabel} e não pode gerar PDF.`,
      });
      return;
    }
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível gerar o PDF',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      return;
    }
    // O conteúdo do PDF é montado pelo back-end a partir do policyId.
    generatePdf.mutate(undefined, {
      onSuccess: () =>
        toaster.create({
          type: 'success',
          title: 'PDF gerado',
          description: 'O download da cotação foi iniciado.',
        }),
      onError: err =>
        toaster.create({
          type: 'error',
          title: 'Erro ao gerar PDF',
          description: err instanceof Error ? err.message : 'Tente novamente.',
        }),
    });
  };

  const handleGenerateProposal = async () => {
    if (isTerminalStatus) {
      toaster.create({
        type: 'warning',
        title: `Cotação ${terminalStatusLabel}`,
        description: rejectionReason
          ? `A cotação foi recusada na subscrição: ${rejectionReason}`
          : `A cotação está ${terminalStatusLabel} e não pode seguir para proposta.`,
      });
      return;
    }
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível gerar a proposta',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      return;
    }
    try {
      const { policyId: proposalPolicyId } =
        await initiateProposal.mutateAsync(policyId);
      // Sem toast de "proposta gerada": aqui só se abre o wizard da proposta.
      // A proposta é gerada no Confirmar do Resumo, e é lá que o sucesso é
      // anunciado.
      void navigate({
        to: '/proposals/$id/pme/edit',
        params: { id: proposalPolicyId || policyId },
        search: { startStep: 'insurance' },
      });
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível gerar a proposta',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  };

  const handleEdit = () => {
    if (!policyId) return;
    void navigate({
      to: '/quotes/$policyId/edit',
      params: { policyId },
      search: { mode: 'edit' },
    });
  };

  return (
    <div className='space-y-6'>
      {finalized && (
        <div className='flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-green-800'>
          <CheckCircle2 className='w-5 h-5' />
          <Text className='text-sm font-semibold'>Cotação finalizada</Text>
        </div>
      )}

      {isUnderAnalysis && (
        <div className='flex items-start gap-3 rounded-md border border-red-300 bg-red-50 p-4 text-red-900'>
          <AlertTriangle className='w-5 h-5 mt-0.5 shrink-0 text-red-600' />
          <div className='space-y-2'>
            <Text className='text-sm'>
              <strong>Atenção!</strong> Cotação encaminhada para análise técnica
              da Alba. O valor apresentado está sujeito a alteração ou declínio
              após avaliação do risco.
            </Text>
          </div>
        </div>
      )}

      {isRejectedByAnalysis && (
        <div className='flex items-start gap-3 rounded-md border border-red-300 bg-red-50 p-4 text-red-900'>
          <AlertTriangle className='w-5 h-5 mt-0.5 shrink-0 text-red-600' />
          <div className='space-y-2'>
            <Text className='text-sm'>
              <strong>Cotação recusada na subscrição.</strong> Esta cotação não
              pode ser finalizada nem seguir para proposta.
            </Text>
            <Text className='text-sm'>
              <strong>Motivo da recusa:</strong>{' '}
              {rejectionReason || 'Motivo não informado.'}
            </Text>
          </div>
        </div>
      )}

      {/* Card Prêmio Mensal — centralizado, gradiente índigo (Figma) */}
      <div
        className='w-full rounded-2xl text-white p-6'
        style={{
          background: 'linear-gradient(180deg, #2C2863 0%, #201D4C 100%)',
        }}
      >
        <div className='relative flex flex-col items-center'>
          <Text className='text-sm text-center text-white'>Prêmio Mensal</Text>
          <button
            type='button'
            onClick={() => setPremiumOpen(o => !o)}
            aria-label={premiumOpen ? 'Recolher' : 'Expandir'}
            className='absolute right-0 top-0 text-white hover:text-white/80'
          >
            {premiumOpen ? (
              <ChevronUp className='w-5 h-5' />
            ) : (
              <ChevronDown className='w-5 h-5' />
            )}
          </button>
          {isCalcLoading && !calc && hasLives ? (
            <div className='mt-3 flex items-center gap-2'>
              <Spinner size='sm' />
              <Text className='text-sm text-white/80'>Calculando prêmio…</Text>
            </div>
          ) : (
            <>
              <Text
                className={`text-3xl font-bold mt-2 text-center text-white ${blurPremiumValueClass}`}
              >
                {formatBRL(monthlyPremium)}
              </Text>
              {!hasLives && (
                <Text className='mt-1 text-center text-sm text-white/80'>
                  Adicione ao menos uma vida para calcular o prêmio.
                </Text>
              )}
            </>
          )}
        </div>

        {premiumOpen && (
          <div className='mt-6'>
            <div className='grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm'>
              <CardInfo
                label='Prêmio Líquido'
                value={formatBRL(monthlyPremiumBeforeIof)}
                valueClassName={blurPremiumValueClass}
              />
              <CardInfo
                label='IOF'
                value={formatBRL(monthlyIof)}
                valueClassName={blurPremiumValueClass}
              />
              <CardInfo label='Periodicidade de pagamento' value='Mensal' />
            </div>
            <div className='my-4 border-t border-white/30' />
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm'>
              <CardInfo label='Produto' value={PRODUCT_LABEL} />
              <CardInfo label='Tipo da Contratação' value={contractType} />
              <CardInfo
                label='Número da Cotação'
                value={`Nº ${proposalNumber || '—'}`}
              />
              <CardInfo label='Validade' value={calc?.validity || '—'} />
            </div>
          </div>
        )}

        {isCalcError && (
          <div className='mt-4 rounded-md bg-white/10 px-3 py-2 text-center text-sm text-white'>
            {calcError instanceof Error
              ? calcError.message
              : 'Falha ao carregar o cálculo da cotação.'}
          </div>
        )}
      </div>

      {/* Versões — cadeia de clonagem (apenas clones) */}
      {quoteDetails?.clonedFrom?.length && policyId ? (
        <CloneVersionsSection
          clonedFrom={quoteDetails.clonedFrom}
          current={{
            policyId,
            kind: 'QUOTE',
            proposalNumber: proposalNumber || quoteDetails.proposalNumber,
            statusPortal: quoteDetails.statusPortal,
          }}
          productId={productId || undefined}
        />
      ) : null}

      {/* Lista de Estipulantes */}
      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          <Text className='font-bold text-alba-black-500'>
            Lista de Estipulantes
          </Text>
          {accordionKeys.length > 0 && (
            <button
              type='button'
              onClick={toggleAll}
              className='flex items-center gap-1 text-sm font-medium text-alba-blue-500 hover:underline'
            >
              {allOpen ? 'Fechar Todos' : 'Abrir Todos'} ({accordionKeys.length}
              )
              {allOpen ? (
                <ChevronUp className='w-4 h-4' />
              ) : (
                <ChevronDown className='w-4 h-4' />
              )}
            </button>
          )}
        </div>

        {/* Estipulante (master) — sempre presente */}
        <AccordionCard
          title='Estipulante'
          subtitle={`${stipulator?.isCaepf ? 'CAEPF' : 'CNPJ'} ${
            stipulatorDocument || '—'
          } · ${livesByTarget[STIPULATOR_TARGET] ?? 0} vida(s)`}
          expanded={!!expanded[STIPULATOR_KEY]}
          onToggle={() => toggleExpanded(STIPULATOR_KEY)}
        >
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm'>
            <Info
              label={stipulator?.isCaepf ? 'CAEPF' : 'CNPJ'}
              value={stipulatorDocument || '—'}
            />
            <Info
              label={stipulator?.isCaepf ? 'Responsável' : 'Razão Social'}
              value={stipulator?.responsibleName || '—'}
            />
            <Info label='E-mail' value={stipulator?.email || '—'} />
            <Info label='Telefone' value={stipulator?.phone || '—'} />
          </div>
          {policyId && (
            <SummaryLivesGrid
              policyId={policyId}
              context='quote'
              targetId={STIPULATOR_TARGET}
            />
          )}
        </AccordionCard>

        {/* Subestipulantes */}
        {subStipulators.map((sub, idx) => (
          <SubStipulatorResumeCard
            key={sub.id}
            index={idx + 1}
            sub={sub}
            livesCount={livesByTarget[sub.id] ?? 0}
            policyId={policyId}
            expanded={!!expanded[sub.id]}
            onToggleExpand={() => toggleExpanded(sub.id)}
          />
        ))}
      </div>

      {/* Comercialização */}
      {commercialization && (
        <div className='rounded-lg border border-alba-gray-200 bg-white p-4'>
          <Text className='font-bold text-alba-black-500 mb-2'>
            Comercialização
          </Text>
          <div className='grid grid-cols-2 gap-3 text-sm lg:grid-cols-4'>
            <Info
              label='Corretagem'
              value={percentOrZero(commercialization.brokeragePercent)}
            />
            <Info
              label='Agenciamento'
              value={percentOrZero(commercialization.agencingPercent)}
            />
            {(commercialization.coBrokers?.length ?? 0) > 0 && (
              <>
                <Info
                  label='Participação do corretor'
                  value={percentOrZero(
                    commercialization.leaderSharePercent || '100,00'
                  )}
                />
                {commercialization.coBrokers?.map((coBroker, index) => (
                  <Info
                    key={coBroker.agentCode}
                    label={
                      (commercialization.coBrokers?.length ?? 0) > 1
                        ? `Co-corretor ${index + 1}`
                        : 'Co-corretor'
                    }
                    value={percentOrZero(coBroker.sharePercent)}
                  />
                ))}
              </>
            )}
            {showUnderwritingPercents && (
              <>
                <Info
                  label='Agravo'
                  value={percentOrZero(commercialization.surchargePercent)}
                />
                <Info
                  label='Desconto atuarial'
                  value={percentOrZero(
                    commercialization.actuarialDiscountPercent
                  )}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Ações */}
      <div className='flex justify-between border-t border-alba-gray-200 pt-6 flex-wrap gap-3'>
        {/* Cotação encerrada não volta para os steps de preenchimento: o
            "Voltar" seria o último caminho aberto para editar o que não vale
            mais. Resta conferir o resumo e sair. */}
        <Button
          variant='ghost'
          size='large'
          onClick={onBack}
          disabled={isStatusUnknown || isTerminalStatus || isVersionSnapshot}
        >
          <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <div className='flex gap-2'>
          {showCalculateButton &&
            !isStatusUnknown &&
            !isUnderAnalysis &&
            !isTerminalStatus &&
            !isVersionSnapshot && (
              <Button
                variant='primary'
                size='large'
                onClick={handleCalculate}
                disabled={calculateQuote.isPending || quoteWriteGuard.blocked}
                title={quoteWriteGuard.reason}
              >
                {calculateQuote.isPending ? (
                  <Spinner size='sm' />
                ) : (
                  <Calculator className='w-5 h-5' strokeWidth={1.6} />
                )}
                Calcular
              </Button>
            )}
          {isStatusUnknown ? (
            <div className='flex items-center px-4'>
              <Spinner size='sm' />
            </div>
          ) : isUnderAnalysis || isTerminalStatus || isVersionSnapshot ? (
            <Button variant='secondary' size='large' onClick={onExit}>
              <LogOut className='w-5 h-5' strokeWidth={1.6} />
              Sair
            </Button>
          ) : canGenerateDocuments ? (
            <>
              <Button
                variant='secondary'
                size='large'
                onClick={handleGeneratePdf}
                disabled={
                  generatePdf.isPending ||
                  !isCalculated ||
                  quoteActionGuard.blocked
                }
                title={quoteActionGuard.reason}
                className='border-alba-green-500 bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:bg-alba-green-600'
              >
                {generatePdf.isPending ? (
                  <Spinner size='sm' />
                ) : (
                  <FileDown className='w-5 h-5' strokeWidth={1.6} />
                )}
                Gerar PDF
              </Button>
              <Button
                variant='primary'
                size='large'
                onClick={handleGenerateProposal}
                disabled={
                  initiateProposal.isPending || proposalWriteGuard.blocked
                }
                title={proposalWriteGuard.reason}
              >
                {initiateProposal.isPending ? (
                  <Spinner size='sm' />
                ) : (
                  <FileText className='w-5 h-5' strokeWidth={1.6} />
                )}
                Gerar Proposta
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='secondary'
                size='large'
                className='border-alba-green-500 bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:bg-alba-green-600'
                onClick={handleEdit}
                disabled={quoteWriteGuard.blocked}
                title={quoteWriteGuard.reason}
              >
                <Pencil className='w-5 h-5' strokeWidth={1.6} />
                Editar
              </Button>
              <Button
                variant='primary'
                size='large'
                onClick={() => setConfirmOpen(true)}
                disabled={quoteWriteGuard.blocked}
                title={quoteWriteGuard.reason}
              >
                <CheckCircle2 className='w-5 h-5' strokeWidth={1.6} />
                Finalizar
              </Button>
              <Button variant='secondary' size='large' onClick={onExit}>
                <LogOut className='w-5 h-5' strokeWidth={1.6} />
                Sair
              </Button>
            </>
          )}
        </div>
      </div>

      <ConfirmFinalizationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmFinalize}
      />

      <UnderAnalysisInfoModal
        isOpen={isUnderAnalysisModalOpen}
        onClose={() => setIsUnderAnalysisModalOpen(false)}
        confirmLabel='Ir para Minhas Cotações'
        onConfirm={() => {
          setIsUnderAnalysisModalOpen(false);
          void navigate({ to: '/quotes' });
        }}
      />
    </div>
  );
}

/** Campo label/valor usado dentro do card escuro de prêmio. */
function CardInfo({
  label,
  value,
  valueClassName = '',
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div>
      <div className='text-white/60 text-xs'>{label}</div>
      <div className={`font-semibold text-white ${valueClassName}`}>
        {value}
      </div>
    </div>
  );
}

/** Campo label/valor padrão (fundo claro). */
/**
 * Percentual não preenchido vale zero — o `??` sozinho não servia porque o back
 * devolve string VAZIA (não `null`) quando a taxa não foi informada, e o
 * template acabava exibindo só " %".
 */
function percentOrZero(value?: string | null): string {
  return `${value?.trim() || '0,00'} %`;
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text className='text-xs uppercase tracking-wider text-alba-gray-500'>
        {label}
      </Text>
      <Text className='text-sm font-semibold text-alba-black-500'>{value}</Text>
    </div>
  );
}

/** Card de accordion genérico (cabeçalho clicável + conteúdo colapsável). */
function AccordionCard({
  title,
  subtitle,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  subtitle?: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className='rounded-lg border border-alba-gray-200 bg-white'>
      <button
        type='button'
        onClick={onToggle}
        className='w-full flex items-center justify-between p-4'
      >
        <div className='text-left'>
          <Text className='font-semibold text-alba-black-500'>{title}</Text>
          {subtitle && (
            <Text className='text-xs text-alba-gray-500'>{subtitle}</Text>
          )}
        </div>
        {expanded ? (
          <ChevronUp className='w-4 h-4 text-alba-gray-600' />
        ) : (
          <ChevronDown className='w-4 h-4 text-alba-gray-600' />
        )}
      </button>
      {expanded && (
        <div className='border-t border-alba-gray-200 p-4 space-y-4'>
          {children}
        </div>
      )}
    </div>
  );
}

function SubStipulatorResumeCard({
  index,
  sub,
  livesCount,
  policyId,
  expanded,
  onToggleExpand,
}: {
  index: number;
  sub: GroupLifeSubStipulator;
  livesCount: number;
  policyId?: string;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const documentLabel = sub.isCaepf ? 'CAEPF' : 'CNPJ';
  const document = formatPartyDocument(sub.cnpj, { isCaepf: sub.isCaepf });

  return (
    <AccordionCard
      title={`Subestipulante ${index} — ${sub.legalName}`}
      subtitle={`${documentLabel} ${document || '—'} · ${livesCount} vida(s)`}
      expanded={expanded}
      onToggle={onToggleExpand}
    >
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm'>
        <Info label={documentLabel} value={document || '—'} />
        <Info label='Razão Social' value={sub.legalName || '—'} />
        <Info label='E-mail' value={sub.email || '—'} />
        <Info label='Telefone' value={sub.phone || '—'} />
      </div>
      {/* Vidas DO SUB: a grade é paginada server-side por `targetId`. */}
      {policyId && (
        <SummaryLivesGrid
          policyId={policyId}
          context='quote'
          targetId={sub.id}
        />
      )}
    </AccordionCard>
  );
}
